const DURATION = 1200;
const EASING = t => 1 - Math.pow(1 - t, 3); // ease-out cubic
const AUTOPLAY_DELAY = 4000; // ms to hold after typing finishes before switching variant

function parseValue(text) {
    const match = text.match(/[\d,]+/);
    return match ? parseInt(match[0].replace(/,/g, ''), 10) : null;
}

function countUp({ el, prefix, suffix, target }, isCancelled) {
    const start = performance.now();
    function tick(now) {
        if (isCancelled()) return;
        const progress = Math.min((now - start) / DURATION, 1);
        el.textContent = `${prefix}${Math.round(EASING(progress) * target)}${suffix}`;
        if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
}

function triggerBars(resultsEl) {
    const bars = resultsEl.querySelectorAll('.mcp-very-kv__result-bar');
    if (!bars.length) return;
    // Disable transition so the reset to scaleX(0) is instant (no shrink flicker)
    bars.forEach(b => { b.style.transition = 'none'; b.style.transform = 'scaleX(0)'; });
    requestAnimationFrame(() => requestAnimationFrame(() =>
        // Restore CSS transition + remove inline transform → CSS scaleX(1) fires the animation
        bars.forEach(b => { b.style.transition = ''; b.style.transform = ''; })
    ));
}

export default class McpVeryKv extends HTMLElement {
    connectedCallback() {
        const inviewEl = this.querySelector('is-inview');
        if (!inviewEl) return;

        this._initCountUp(inviewEl);
        this._initVariantCycling(inviewEl);
    }

    disconnectedCallback() {
        clearTimeout(this._cycleTimeout);
    }

    _initCountUp(inviewEl) {
        // Parse and store original values once — textContent is still the source
        // text at connectedCallback time (before any countUp has run)
        this._countUpData = [...this.querySelectorAll('.mcp-very-kv__result-value')].map(el => {
            const original = el.textContent;
            const target = parseValue(original);
            if (target === null) return null;
            const prefix = original.slice(0, original.search(/\d/));
            const suffix = original.slice(original.search(/\d/) + String(target).length);
            return { el, prefix, suffix, target };
        }).filter(Boolean);

        this._countUpGen = 0;

        if (inviewEl.classList.contains('in-view')) {
            this._runCountUp();
        } else {
            const mo = new MutationObserver(() => {
                if (inviewEl.classList.contains('in-view')) {
                    mo.disconnect();
                    this._runCountUp();
                }
            });
            mo.observe(inviewEl, { attributes: true, attributeFilter: ['class'] });
        }
    }

    _runCountUp() {
        const gen = ++this._countUpGen;
        const isCancelled = () => gen !== this._countUpGen;
        this._countUpData.forEach(item => countUp(item, isCancelled));
    }

    // Flip every variant element to `index` at once, then (re)start that
    // variant's typing. The typing-text:done event drives the next switch.
    _activate(index) {
        this._prompts.forEach((el, i) => el.classList.toggle('is-active', i === index));
        this._results.forEach((el, i) => el.classList.toggle('is-active', i === index));

        // The first (bar-chart) variant re-runs its bar + count-up animation
        // when it comes back into view, after the card has faded in.
        if (index === 0) {
            setTimeout(() => {
                triggerBars(this._results[0]);
                this._runCountUp();
            }, 700);
        }

        this._typePrompt(index);
    }

    _typePrompt(index) {
        const tt = this._prompts[index].querySelector('typing-text');
        if (tt?.reset) tt.reset();
    }

    _initVariantCycling(inviewEl) {
        this._prompts = [...this.querySelectorAll('.mcp-very-kv__prompt-group > .mcp-very-kv__chat-prompt')];
        this._results = [...this.querySelectorAll('.mcp-very-kv__results-group > .mcp-very-kv__results')];

        if (this._prompts.length <= 1) return;

        let current = 0;

        // Single source of truth: when the active prompt finishes typing, hold
        // for AUTOPLAY_DELAY, then advance. Ignore stray events from any prompt
        // that isn't the current one.
        this.addEventListener('typing-text:done', e => {
            if (e.target.closest('.mcp-very-kv__chat-prompt') !== this._prompts[current]) return;
            clearTimeout(this._cycleTimeout);
            this._cycleTimeout = setTimeout(() => {
                current = (current + 1) % this._prompts.length;
                this._activate(current);
            }, AUTOPLAY_DELAY);
        });

        // First variant is already active in markup — just start its typing.
        const startCycle = () => this._typePrompt(0);

        if (inviewEl.classList.contains('in-view')) {
            startCycle();
        } else {
            const mo = new MutationObserver(() => {
                if (inviewEl.classList.contains('in-view')) {
                    mo.disconnect();
                    startCycle();
                }
            });
            mo.observe(inviewEl, { attributes: true, attributeFilter: ['class'] });
        }
    }
}
